import type React from 'react';
import { useState } from 'react';
import { RequestDetailModal } from './RequestDetailModal';
import styles from './BusinessFlow.module.css';

interface CollectionRequestListProps {
  items: any[];
}

export const CollectionRequestList: React.FC<CollectionRequestListProps> = ({ items }) => {
  const [collapsedFolders, setCollapsedFolders] = useState<Record<string, boolean>>({});
  const [selectedRequest, setSelectedRequest] = useState<any>(null);

  const toggleFolder = (folderName: string): void => {
    setCollapsedFolders((prev) => ({
      ...prev,
      [folderName]: !prev[folderName],
    }));
  };

  const handleRequestClick = (request: any, order: number): void => {
    setSelectedRequest({ ...request, order });
  };

  const countRequests = (requestItems: any[]): number => {
    let count = 0;
    for (const subItem of requestItems) {
      if (subItem.request) count++;
      if (subItem.item) count += countRequests(subItem.item);
    }
    return count;
  };

  const renderRequestItems = (
    requestItems: any[],
    parentPath: string = '',
    startOrder: number = 1,
  ): React.ReactNode[] | null => {
    if (!requestItems || !Array.isArray(requestItems)) return null;

    let currentOrder = startOrder;
    const elements: React.ReactNode[] = [];

    for (let i = 0; i < requestItems.length; i++) {
      const item = requestItems[i];

      if (item.item && Array.isArray(item.item)) {
        const folderPath = `${parentPath}/${item.name}`;
        const isCollapsed = collapsedFolders[folderPath];

        elements.push(
          <div className={styles.folderItem} key={i}>
            <div className={styles.folderHeader} onClick={() => toggleFolder(folderPath)}>
              <span>{isCollapsed ? '▶' : '▼'}</span>📁 {item.name}
            </div>
            <div className={styles.folderContent} data-collapsed={isCollapsed ? 'true' : 'false'}>
              {renderRequestItems(item.item, folderPath, currentOrder)}
            </div>
          </div>,
        );

        currentOrder += countRequests(item.item);
      } else if (item.request) {
        const method = item.request.method || 'GET';
        const url = typeof item.request.url === 'string' ? item.request.url : item.request.url?.raw || '';
        const order = currentOrder;

        elements.push(
          <div className={styles.requestItem} key={i} onClick={() => handleRequestClick(item, order)}>
            <span className={styles.requestOrder}>{order}</span>
            <span className={styles.methodBadge} data-method={method.toUpperCase()}>
              {method}
            </span>
            <div className={styles.requestInfo}>
              <div className={styles.requestName}>{item.name}</div>
              <div className={styles.requestUrl}>{url}</div>
            </div>
          </div>,
        );
        currentOrder++;
      }
    }

    return elements;
  };

  return (
    <>
      <div className={styles.section}>
        <h2 className={styles.sectionTitle}>📋 Collection Requests</h2>
        <div className={styles.requestsList}>{renderRequestItems(items)}</div>
      </div>

      {selectedRequest && <RequestDetailModal request={selectedRequest} onClose={() => setSelectedRequest(null)} />}
    </>
  );
};
